import moment, { Moment } from 'moment';
import elementResizeDetectorMaker from 'element-resize-detector';
import { RouteDtoModelType } from '../../../../../store/mst/models';
import { defaultDateTimeFormat } from '../../../../../constants';
import { CustomRouteItem } from '../index';
import { getRouteGanttEndMoment } from './routeGanttTimes';
import { EventType, OPACITY_25 } from '../../constants';

interface IdleTimes {
  startTime: Moment,
  endTime: Moment
}

export const resizeDetector = {
  addListener: (component: any) => {
    component.erd = elementResizeDetectorMaker({
      strategy: 'scroll',
    });
    component.erd.width = component.container.offsetWidth;
    component.erd.listenTo(component.container, (element: any) => {
      const width = element.offsetWidth;
      if (!component.erdWidth || component.erdWidth < width) {
        component.resize(component.props);
        component.erdWidth = width;
      }
    });
  },
  removeListener: (component: any) => {
    component?.erd?.removeAllListeners(component.container);
  },
} as any;

const getCommonRouteItemFields = (route: RouteDtoModelType) => ({
  title: '',
  group: route.id,
  driverName: route.driver?.name,
});

const getIdleTimesOnly = (route: RouteDtoModelType): IdleTimes[] => {
  const idleEvents = route.timeline.filter((timelineEvent) => timelineEvent.eventType === EventType.IDLING);
  return idleEvents.map((idleEvent) => ({
    startTime: idleEvent.startTimeMoment,
    endTime: idleEvent.endTimeMoment,
  } as IdleTimes));
};

export const createRouteLineSegment = (route: RouteDtoModelType, startTime?: string, endTime?: string): CustomRouteItem => {
  const startMoment = startTime ? moment(startTime, defaultDateTimeFormat) : undefined;
  const endMoment = getRouteGanttEndMoment(startTime, endTime);

  return {
    ...getCommonRouteItemFields(route),
    id: `${route.uuid || route.id}-segment-${startTime}`,
    canChangeGroup: false,
    canMove: false,
    canResize: 'both',
    canCluster: false,
    start_time: startMoment,
    end_time: endMoment,
    route,
    routeLine: true,
    itemProps: {
      style: {
        color: `rgba(${route.color.rgb})`,
        outlineColor: `rgba(${route.color.rgb})`,
        backgroundColor: `rgba(${route.color.rgb})`,
        maxHeight: '2px',
        minHeight: '2px',
      },
    },
  };
};

export const getRouteLineSegments = (route: RouteDtoModelType): CustomRouteItem[] => {
  const idleTimes: any[] = getIdleTimesOnly(route);
  const routeLines: CustomRouteItem[] = [];
  const idleSize = idleTimes.length;
  const routeStartTime = route.startEvent?.departureTime || route.firstStopEvent?.startTime || undefined;
  const routeEndTime = route.endEvent?.arrivalTime || route.lastStopEvent?.startTime || undefined;

  if (idleSize !== 0) {
    const startingRouteLineSegment = createRouteLineSegment(route, routeStartTime, idleTimes[0].startTime);
    const endingRouteLineSegment = createRouteLineSegment(route, idleTimes[idleSize - 1].endTime, routeEndTime);
    for (let index = 0; index < idleSize; index += 1) {
      if (idleTimes[index + 1]) {
        const lineSegment = createRouteLineSegment(route, idleTimes[index].endTime, idleTimes[index + 1].startTime);
        routeLines.push(lineSegment);
      }
    }
    return [startingRouteLineSegment, ...routeLines, endingRouteLineSegment];
  }
  return [createRouteLineSegment(route, routeStartTime, routeEndTime)];
};

export const getRouteShiftItems = (route: RouteDtoModelType): CustomRouteItem => ({
  ...getCommonRouteItemFields(route),
  id: `${route.uuid || route.id}-${EventType.SHIFT_TIME}`, // event type just used as a unique identifier so not to clash with getRouteLine
  canChangeGroup: false,
  canMove: false,
  canCluster: false,
  canResize: 'both',
  start_time: route.shiftStartTime || undefined,
  end_time: route.shiftEndTime,
  eventType: EventType.SHIFT_TIME,
  route,
  routeLine: false,
  itemProps: {
    style: {
      backgroundColor: `rgba(${route.color.rgb},${OPACITY_25})`,
      height: '24px',
    },
  },
});

export const getRouteStopItems = (route: RouteDtoModelType): CustomRouteItem[] => {
  const routeTimelineLength = route.timeline.length;
  let addExtraTime = false;
  // If there are not routes, then there is only a start and finish. If the start time is equal to the finish time, we want to stop the icons from overlapping.
  if (routeTimelineLength === 2 && route.timeline[0].startTimeMoment?.isSame(route.timeline[1].startTimeMoment)) {
    addExtraTime = true;
  }

  return route.timeline.map((timelineEvent, index) => {
    const isLastStop = index === routeTimelineLength - 1;

    return {
      ...getCommonRouteItemFields(route),
      id: timelineEvent.id,
      canChangeGroup: true,
      canMove: !timelineEvent.isStopEvent,
      start_time: addExtraTime && isLastStop ? timelineEvent.startTimeMoment?.clone().add(15, 'minutes') : timelineEvent.startTimeMoment,
      end_time: addExtraTime && isLastStop ? timelineEvent.endTimeMoment?.clone().add(15, 'minutes') : timelineEvent.endTimeMoment,
      routeLine: false,
      route,
      canCluster: timelineEvent.isStopEvent,
      routeTimeline: timelineEvent,
      eventUuid: timelineEvent.eventUuid,
      eventType: timelineEvent.eventType as EventType,
      visitId: timelineEvent.orders?.[0]?.id,
      visitName: timelineEvent.orders?.[0]?.name ?? undefined,
      showVisit: timelineEvent.orders?.[0],
      itemProps: {
        style: {
          color: `rgba(${route.color.rgb})`,
          backgroundColor: `rgba(${route.color.rgb}, 1)`,
          outlineColor: `rgba(${route.color.rgb}, 1)`,
        },
      },
    };
  });
};

export const getRouteIdleItems = (route: RouteDtoModelType): CustomRouteItem[] => route.timeline
  .filter((timelineEvent) => timelineEvent.eventType === EventType.IDLING).map((idleEvent) => ({
    ...getCommonRouteItemFields(route),
    id: `${idleEvent.id}-${EventType.IDLING}`,
    canChangeGroup: false,
    canResize: 'both',
    canMove: false,
    canCluster: false,
    start_time: idleEvent.startTimeMoment,
    end_time: idleEvent.endTimeMoment,
    routeLine: false,
    route,
    routeTimeline: idleEvent,
    eventType: idleEvent.eventType as EventType,
    itemProps: {
      style: {
        backgroundColor: 'transparent',
      },
    },
  } as CustomRouteItem));

export const getRoutePauseItems = (route: RouteDtoModelType): CustomRouteItem[] => route.timeline
  .filter((timelineEvent) => timelineEvent.eventType === EventType.PAUSED).map((pauseEvent) => ({
    ...getCommonRouteItemFields(route),
    id: `${pauseEvent.id}-${EventType.PAUSED}`,
    canChangeGroup: false,
    canResize: false,
    canMove: false,
    canCluster: false,
    start_time: pauseEvent.startTimeMoment,
    end_time: pauseEvent.endTimeMoment,
    routeLine: false,
    route,
    routeTimeline: pauseEvent,
    eventType: pauseEvent.eventType as EventType,
    itemProps: {
      style: {
        color: `rgba(${route.color.rgb})`,
        backgroundColor: `rgba(${route.color.rgb}, 1)`,
        outlineColor: `rgba(${route.color.rgb}, 1)`,
      },
    },
  } as CustomRouteItem));
